<template>
  <div>
    <div v-if="! isModal" class="level is-variable bd-klmn-columns is-1 is-marginless">
      <div class="level-left">
        <b-tooltip :label="$t('clearFilter')" type="is-dark" position="is-right">
          <b-button
            class="filter-button is-hidden-mobile"
            size="is-circle"
            icon-left="filter-remove"
            @click="clearFilter"
          />
        </b-tooltip>
        <div class="filter-tags">
          <b-tag
            v-for="(filter, index) in filters"
            :key="index"
            type="is-purple"
            closable
            @close="removeFilter(filter)"
          >
            {{ $t('filter.' + filter) }}
          </b-tag>
        </div>
      </div>
      <div class="level-right">
        <slot name="header-right" />
      </div>
    </div>
    <b-table
      :data="data"
      :loading="isLoading"

      :current-page.sync="page"
      :total="total"
      :per-page="perPage"

      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      striped
      sticky-header
      paginated
      hoverable
      backend-sorting
      backend-pagination

      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"

      class="no-wrap custom-table"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <template slot-scope="props">
        <b-table-column custom-key="actions" class="is-actions-cell" sticky>
          <b-tooltip v-if="checkPermission('delete_service_record')" :label="$t('delete')" type="is-dark" position="is-right">
            <b-button
              outlined
              size="is-small"
              class="button-green is-danger mr-5 only-icon"
              icon-left="close-circle"
              @click="deleteServiceRecord(props.row.id)"
            />
          </b-tooltip>

          <b-button outlined size="is-small" class="button-purble btn-details" icon-left="arrow-right-bold"
                    tag="a" :href="serviceRecordURL(props.row.id)"
                    :target="serviceRecordTarget"
                    @click.prevent="showServiceRecord(props.row.id)"
          >
            {{ $t('details') }}
          </b-button>
        </b-table-column>

        <b-table-column :label="$t('filter.id')" field="id" number sortable>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="id-search search-dropdown" :close-on-click="false" trap-focus>
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.id" :placeholder="$t('idSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>

            {{ column.label }}
          </template>
          #{{ props.row.id }}
        </b-table-column>

        <b-table-column :label="$t('filter.plate')" field="car_plate">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.plate" :placeholder="$t('plateSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small v-if="props.row.car" class="tag is-abbr-like">
            {{ props.row.car.plate }}
          </small>
        </b-table-column>

        <b-table-column label="Talep Tarihi" field="service_date" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.service_date_range"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.service_date_range = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small class="tag is-abbr-like" :title="props.row.service_date">
            {{ props.row.service_date | formatDate }}
          </small>
        </b-table-column>

        <b-table-column label="Talep Türü" field="service_type">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.service_type" placeholder="Talep Türü" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.service_type_specification.name }}
        </b-table-column>

        <b-table-column label="Talep Eden Birim" field="request_unit">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.request_unit" placeholder="Talep Eden Birim" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.request_unit_specification.name }}
        </b-table-column>

        <b-table-column label="Talep Eden Kişi" field="demandant_name" sortable>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.demandant_name" :placeholder="$t('nameSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <b-tooltip
            :label="props.row.demandant_name"
            :active="props.row.demandant_name.length > 20"
            type="is-dark"
            position="is-bottom"
          >
            {{ props.row.demandant_name | limitShow }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Marka" field="brand">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.brand" placeholder="Marka Ara" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.brand }}
          </template>
        </b-table-column>

        <b-table-column label="Model" field="model">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.model" placeholder="Model Arama" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.model }}
          </template>
        </b-table-column>

        <b-table-column label="Cinsi" field="type">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.type" placeholder="Cins Arama" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.type }}
          </template>
        </b-table-column>

        <b-table-column label="Koltuk Sayısı" field="number_of_seats">
          <template v-if="props.row.car">
            {{ props.row.car.number_of_seats }}
          </template>
        </b-table-column>

        <b-table-column label="Oluşturulma Tarihi" field="created_at" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.created_at_range"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.created_at_range = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small class="tag is-abbr-like" :title="props.row.created_at">
            {{ props.row.created_at | formatDate }}
          </small>
        </b-table-column>

        <b-table-column label="Oluşturan Kullanıcı" field="user_name">
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.user_name" :placeholder="$t('nameSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <b-tooltip
            v-if="props.row.user"
            :label="props.row.user.name"
            :active="props.row.user.name.length > 20"
            type="is-dark"
            position="is-bottom"
          >
            {{ props.row.user.name | limitShow }}
          </b-tooltip>
        </b-table-column>
      </template>

      <template slot="bottom-left">
        <div v-if="total > 0" class="has-text-left subtitle help">
          {{ $t('paginationShowing', [total.toLocaleString(), ((page * perPage) - perPage) + 1, ((page * perPage) > total ? total : (page * perPage)) ]) }}
        </div>
      </template>

      <section slot="empty" class="section">
        <div class="content has-text-grey has-text-centered">
          <template v-if="!isLoading">
            <p>
              <b-icon icon="table" size="is-large" />
            </p>
            <p>{{ $t('nodata') }}</p>
          </template>
        </div>
      </section>
    </b-table>

    <b-modal
      :active.sync="isTransferModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      animation=""
      aria-modal
    >
      <div class="modal-card full-width" style="width: auto">
        <section class="modal-card-body full-width">
          <service-record-details
            :id="serviceRecordId"
            :isModal="true"
            :carId="carId"
          />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import download from 'downloadjs'
import debounce from 'lodash/debounce'
import $ from 'jquery'

export default {
  components: {
    ServiceRecordDetails: () => import(/* webpackPrefetch: true */ '@/views/ServiceRecordDetails'),
  },
  props: {
    dataUrl: {
      type: String,
      default: null
    },
    checkable: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    },
    where: {
      type: Object,
      default: () => {}
    },
    carId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      isTransferModalActive: false,
      serviceRecordId: null,
      data: [],
      isLoading: false,
      perPage: 50,
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      total: 0,
      page: 1,
      count: 0,
      inputTrigger: null,
      queryData: {
        id: '',
        user_name: '',
        demandant_name: '',
        brand: '',
        model: '',
        plate: '',
        type: '',
        service_date_range: [],
        created_at_range: [],
        excel: false,
      },
      filters: [],
    }
  },
  computed: {
    ...mapState([
      'boardRefresh',
    ]),
    serviceRecordTarget () {
      if (this.isModal) {
        return '_blank'
      }

      return '_self'
    },
  },
  watch: {
    query () {
      this.filterAction()
    },
    queryData: {
      handler () {
        this.filterAction()
      },
      deep: true
    },
    'queryData.service_date_range' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.created_at_range' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.id' (n) {
      if (n.indexOf('#') > -1) {
        this.queryData.id = n.replace('#', '');
      }
    },
    boardRefresh (n) {
      if (n == true) {
        this.loadTransfers()
      }
    }
  },
  mounted () {
    if (this.where) {
      this.queryData = this.$_.merge(this.queryData, this.where);
    }
    this.isLoading = true
    this.loadTransfers()
  },
  methods: {
    filterAction: debounce(function () {
      this.filterShow()
      this.loadTransfers()
    }, 350),

    clearFilter () {
      this.queryData = {
        id: '',
        user_name: '',
        demandant_name: '',
        brand: '',
        model: '',
        plate: '',
        type: '',
        service_date_range: [],
        created_at_range: [],
        excel: false,
      }
      this.filterShow()
    },
    loadTransfers () {
      if (!this.dataUrl) {
        return
      }

      this.$emit('loadingChange', true)

      this.$http({
        url: this.dataUrl,
        method: 'GET',
        responseType: this.queryData.excel ? 'blob' : 'json',
        params: {
          ...this.queryData,
          sort_by: this.sortField,
          sort_order: this.sortOrder,
          page: this.page,
          created_at_range: this.queryData.created_at_range.length === 2
            ? [moment(this.queryData.created_at_range[0]).format('YYYY-MM-DD'), moment(this.queryData.created_at_range[1]).format('YYYY-MM-DD')]
            : null,
          service_date_range: this.queryData.service_date_range.length === 2
            ? [moment(this.queryData.service_date_range[0]).format('YYYY-MM-DD'), moment(this.queryData.service_date_range[1]).format('YYYY-MM-DD')]
            : null,
        },
      }).then(({ data }) => {
        if (this.queryData.excel) {
          download(data, `service-records-${moment().format('DD-MM-YYYY-h-mm')}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
          this.isLoading = false
          this.$emit('excelChange', false)
          this.$emit('loadingChange', false)
          this.queryData.excel = false
          return false
        }

        this.data = data.data ?? []
        this.total = data.meta.total ?? 0
        this.perPage = data.meta.per_page ?? this.perPage
        this.page = data.meta.current_page ?? this.page
        this.count = data.meta.to ?? this.count

        if (data.data.length > 0) {
          window.scrollTo(0, 0)
        }
      }).catch(e => {
        this.data = []
        this.$buefy.toast.open({
          message: `Error: ${e.message}`,
          type: 'is-danger'
        })
      }).then(() => {
        this.$nextTick(() => {
          this.isLoading = false
          this.$emit('loadingChange', false)

          this.$store.commit('boardRefresh', false)
          this.jqInputTrigger()
        })
      })
    },
    jqInputTrigger () {
      if (this.inputTrigger) return;

      $('.dropdown-trigger').click(function (e) {
        e.stopPropagation()
        $(this).parent().find('input, select').first().focus()
      })
      $('.search-dropdown input').keypress(function (e) {
        if (e.which == '13') {
          $('body').trigger('click')
        }
      });
      $('.dropdown').click(function (e) {
        e.stopPropagation()
      })
      this.inputTrigger = true;
    },
    onPageChange (page) {
      this.page = page
      this.loadTransfers()
    },
    onSort (field, order) {
      this.page = 1
      this.sortField = field
      this.sortOrder = order
      this.loadTransfers()
    },
    type (value) {
      const number = parseFloat(value)
      if (number < 6) {
        return 'is-danger'
      } else if (number >= 6 && number < 8) {
        return 'is-warning'
      } else if (number >= 8) {
        return 'is-success'
      }
    },
    filterShow () {
      this.filters = this.$_.keys(this.$_.omitBy(this.queryData, this.$_.isEmpty))
    },
    removeFilter (filter) {
      this.queryData[filter] = this.$_.isArray(this.queryData[filter]) ? [] : '';
      this.filterShow()
    },
    serviceRecordURL (serviceRecordId) {
      let object = {
        name: 'service_records.show',
        params: { id: serviceRecordId },
        query: {}
      }

      return this.$router.resolve(object).href
    },
    deleteServiceRecord (id) {
      this.$buefy.dialog.confirm({
        message: 'Emin misiniz?',
        cancelText: this.$t('no'),
        confirmText: this.$t('yes'),
        type: 'is-danger',
        onConfirm: () => {
          this.$http
            .delete('service-records/' + id)
            .then(() => {
              this.$buefy.toast.open({
                message: this.$t('success'),
                type: 'is-warning'
              })
              this.$store.commit('boardRefresh', true)
            })
        }
      })
    },
    showServiceRecord (serviceRecordId) {
      this.serviceRecordId = serviceRecordId;
      this.isTransferModalActive = true;
    },
    exportExcel () {
      this.queryData.excel = true
    },
  }
}
</script>
